import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { localISO } from '@/lib/localDate';
import { Bell, Navigation, Phone, Check, RefreshCw } from 'lucide-react';
import { dispatchFetch, T, F, bd, fmtTime, initials } from '@/lib/dispatchApi';
import DispatchButton from "@/components/dispatch/DispatchButton";
import { I } from '@/lib/bricks';

function todayISO() { return localISO(); }

function minutesUntil(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const now = new Date();
  const target = new Date(now);
  target.setHours(h, m, 0, 0);
  const diff = target.getTime() - now.getTime();
  return diff > 0 ? Math.round(diff / 60000) : null;
}

// 499 -> "8 h 19 min", 45 -> "45 min", 120 -> "2 h".
export function fmtUntil(mins) {
  if (mins == null) return '';
  const h = Math.floor(mins / 60), m = mins % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

const jobKind = (k) => (k ? k.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : '');

export default function DispatchTodayPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const today = todayISO();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, notifRes] = await Promise.all([
        dispatchFetch('/api/vendor-os/jobs'),
        dispatchFetch('/api/notifications/unread-count'),
      ]);
      if (!jobsRes.ok) throw new Error(`Jobs ${jobsRes.status}`);
      const allJobs = await jobsRes.json();
      const todayJobs = allJobs
        .filter(j => j.scheduled_date === today)
        .sort((a, b) => (a.time_window_start || '').localeCompare(b.time_window_start || ''));
      setJobs(todayJobs);
      if (notifRes.ok) {
        const n = await notifRes.json();
        setUnread(n.count || 0);
      }
    } catch (e) {
      console.error('Today fetch', e);
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const dateLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const nextJob = jobs.find(j => j.status !== 'completed');
  // Once every shoot is done, keep them listed instead of showing an empty page.
  const laterJobs = nextJob ? jobs.filter(j => j.id !== nextJob.id) : jobs;
  const allDone = jobs.length > 0 && !nextJob;
  const minsUntil = nextJob ? minutesUntil(nextJob.time_window_start) : null;

  const openMaps = (job) => {
    const addr = encodeURIComponent(`${job.address}, ${job.city}, ${job.state}`);
    window.open(`https://maps.google.com/?q=${addr}`, '_blank');
  };

  const px = { paddingLeft: 24, paddingRight: 24 };

  return (
    <div style={{ backgroundColor: T.night, minHeight: '100%', paddingTop: 16, paddingBottom: 32, color: T.moon, fontFamily: F.heading }}>
      {/* Header */}
      <div style={{ ...px, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <p style={{ fontSize: 13, color: T.moonFaint, margin: '0 0 4px 0' }}>{dateLabel}</p>
          <p style={{ fontSize: 26, color: T.moon, fontFamily: F.heading, letterSpacing: -0.5, margin: 0 }}>
            {loading ? '—' : jobs.length > 0 ? `${jobs.length} shoot${jobs.length !== 1 ? 's' : ''}.` : 'Clear day.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            className="bx-tap"
            href="https://www.brxs.ca/help/photographers"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Help for photographers (opens the help centre in a new tab)"
            title="Help"
            data-testid="dispatch-help-link"
            style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <I name="help" size={18} color={T.moonMute} />
          </a>
          <button
            className="bx-tap"
            onClick={fetchData}
            aria-label="Refresh"
            style={{ background: 'none', border: 'none', width: 44, height: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.moonMute, cursor: 'pointer' }}
          >
            <RefreshCw size={16} color={T.moonMute} strokeWidth={1.5} />
          </button>
          <button
            className="bx-tap"
            onClick={() => navigate('/dispatch/notifications')}
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
            style={{ position: 'relative', background: 'none', border: 'none', width: 44, height: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <Bell size={18} color={T.moonMute} strokeWidth={1.5} />
            {unread > 0 && (
              <span style={{
                position: 'absolute', top: 6, right: 4,
                backgroundColor: T.brick, borderRadius: 8,
                minWidth: 15, height: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, color: '#14110E', fontFamily: F.mono, paddingLeft: 3, paddingRight: 3,
              }}>
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
        </div>
      </div>

      {!loading && jobs.length === 0 ? (
        <div style={{ ...px, paddingTop: 60, textAlign: 'center' }}>
          <p style={{ color: T.moonFaint, fontSize: 13, marginBottom: 20 }}>No jobs scheduled for today.</p>
          <DispatchButton variant="main" data-testid="today-schedule" onClick={() => navigate('/dispatch/schedule')}>See this week</DispatchButton>
        </div>
      ) : (
        <>
          {/* Next up card */}
          {nextJob && (
            <div style={{ ...px, marginBottom: 28 }}>
              <p style={{ fontFamily: F.heading, fontSize: 13, fontWeight: 500, color: T.moonMute, letterSpacing: 0, marginBottom: 10 }}>
                {minsUntil ? `Next up · in ${fmtUntil(minsUntil)}` : 'Next up · now'}
              </p>
              <div
                className="bx-tap"
                onClick={() => navigate(`/dispatch/job/${nextJob.id}`)}
                style={{ backgroundColor: T.nightSoft, ...bd(T.nightHairS), borderRadius: 2, padding: 18, cursor: 'pointer' }}
              >
                <p style={{ fontSize: 22, color: T.moon, fontFamily: F.heading, letterSpacing: -0.4, marginBottom: 4 }}>
                  {nextJob.address}
                </p>
                <p style={{ fontSize: 13, color: T.moonFaint, marginBottom: 16 }}>
                  {[nextJob.city, nextJob.state].filter(Boolean).join(', ')}
                  {nextJob.job_type ? ` · ${jobKind(nextJob.job_type)}` : ''}
                </p>

                <div style={{ display: 'flex', ...bd(T.nightHair), borderRadius: 2, marginBottom: 14, overflow: 'hidden' }}>
                  {[
                    ['Arrive', fmtTime(nextJob.time_window_start)],
                    ['Agent', nextJob.agent_name ? initials(nextJob.agent_name) : '—'],
                    ['Package', nextJob.package_name || '—'],
                  ].map(([label, value], i) => (
                    <div key={label} style={{ flex: 1, padding: 10, ...(i > 0 ? { borderLeft: `1px solid ${T.nightHair}` } : {}) }}>
                      <p style={{ fontSize: 12, color: T.moonFaint, marginBottom: 5 }}>{label}</p>
                      <p style={{ fontFamily: F.mono, fontSize: 13, color: T.moon, margin: 0 }}>{value}</p>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <DispatchButton variant="main" data-testid="today-navigate" icon={Navigation} onClick={(e) => { e.stopPropagation(); openMaps(nextJob); }} style={{ flex: 1 }}>Navigate</DispatchButton>
                  {nextJob.agent_phone && (
                    <a
                      href={`tel:${nextJob.agent_phone}`}
                      style={{ width: 48, height: 48, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${T.graphite}`, borderRadius: 2, textDecoration: 'none' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Phone size={15} color={T.moonMute} strokeWidth={1.5} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pre-shoot checklist */}
          {nextJob && nextJob.checklist?.length > 0 && (
            <div style={{ ...px, marginBottom: 28 }}>
              <p style={{ fontSize: 13, fontWeight: 500, color: T.moonMute, marginBottom: 10 }}>Pre-shoot checklist</p>
              <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2 }}>
                {nextJob.checklist.slice(0, 5).map((item, i, arr) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', ...(i < arr.length - 1 ? { borderBottom: `1px solid ${T.nightHair}` } : {}) }}>
                    <div style={{
                      width: 17, height: 17, borderRadius: 2, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      ...(item.completed ? { backgroundColor: T.moonMute } : { ...bd(T.nightHairS, 1.5) }),
                    }}>
                      {item.completed && <Check size={9} color={T.night} strokeWidth={3} />}
                    </div>
                    <span style={{ flex: 1, fontSize: 13, color: item.completed ? T.moonFaint : T.moon, textDecoration: item.completed ? 'line-through' : 'none' }}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Later today */}
          {laterJobs.length > 0 && (
            <div style={{ ...px, marginBottom: 28 }}>
              {allDone && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 14, color: T.moon, marginBottom: 12 }}>All of today's shoots are done.</p>
                  <DispatchButton variant="main" data-testid="today-upload" full onClick={() => navigate('/dispatch/upload')}>Upload files</DispatchButton>
                </div>
              )}
              <p style={{ fontSize: 13, fontWeight: 500, color: T.moonMute, marginBottom: 10 }}>{allDone ? 'Done today' : 'Later today'}</p>
              <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2 }}>
                {laterJobs.map((job, i) => {
                  const payout = job.payout_display_cad != null ? Math.round(job.payout_display_cad) : (job.payout_model === 'owner_full' && job.package_price ? Math.round(job.package_price) : null);
                  return (
                    <div
                      key={job.id}
                      className="bx-tap"
                      onClick={() => navigate(`/dispatch/job/${job.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, cursor: 'pointer', ...(i < laterJobs.length - 1 ? { borderBottom: `1px solid ${T.nightHair}` } : {}) }}
                    >
                      <span style={{ fontFamily: F.mono, fontSize: 13, color: T.moon, width: 52, flexShrink: 0 }}>{fmtTime(job.time_window_start)}</span>
                      <div style={{ width: 1, height: 28, backgroundColor: T.nightHair, flexShrink: 0 }} />
                      <div style={{ flex: 1, paddingLeft: 4 }}>
                        <p style={{ fontSize: 14, color: T.moon, marginBottom: 2 }}>{job.address}</p>
                        <p style={{ fontSize: 12, color: T.moonFaint, margin: 0 }}>{jobKind(job.job_type)}</p>
                      </div>
                      {payout != null && (
                        <span style={{ fontFamily: F.mono, fontSize: 13, color: T.moon }}>
                          ${payout}{job.payout_model === 'owner_full' ? ' · $0' : ''}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
